import { useEffect, useRef, useState } from "react";
import { IMedia, LayoutCommonProps } from "../../../types";
import * as Styled from "./Layout6.styled";
import { ArrowLeft, ArrowRight } from 'react-bootstrap-icons';
import { StarFill } from "react-bootstrap-icons";
import { BigCard } from "../../media-card/big-card";
import { SidebarCard } from "../../media-card/small-card";
import { SeeCard } from "../../media-card/see-card";
import LayoutModal from "../../media-card/FullScreenModal";
import { LayoutHeader } from "../LayoutHeader";
import { scrollOnce, startContinuousScroll, stopContinuousScroll, toTitleCase, truncateWords } from "../../../libs/utils";
import { colors } from "../../../libs/themes";
import RatingToast from "../RatingToast";

export const Layout6: React.FC<LayoutCommonProps> = ({
  mediaItems,
  mediaRating,
  onWatchClick,
  onRatingSuccess,
  isSignedIn,
  sessionToken,
  activePlans,
  hasFullAccess,
  freeTrialPlanIds,
  playlistTitle,
  enableCompetitionMode,
  enableRatingControls,
  refreshMediaRatings
}) => {
  const bigCardScrollRef = useRef<HTMLDivElement>(null);
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mainMedia, setMainMedia] = useState<IMedia | undefined>(undefined);
  const [toastCardId, setToastCardId] = useState<string | null>(null);
  const handleRatingSuccessLocal = (cardId: string) => {
    setToastCardId(cardId);
    onRatingSuccess();
    refreshMediaRatings?.()
    setTimeout(() => setToastCardId(null), 5000);
  };

  useEffect(() => {
    if (mediaItems.length && !mainMedia) {
      const firstMedia = mediaItems[0];
      setMainMedia(firstMedia);
    }
  }, [mediaItems, mainMedia]);


  const handleSeeAllClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const onCardClick = (item: IMedia) => {
    setMainMedia(item);
  };

  if (!mediaItems.length || !mainMedia) return null;

  const bigCardMedia = mediaItems.slice(0, 3);
  const remainingMedia = mediaItems.slice(3);

  return (
    <>
      <LayoutHeader
        title={playlistTitle}
        itemCount={mediaItems?.length}
        onSeeAll={handleSeeAllClick}
        padding="20px 0px 0px 0px"
        marginTop="0px"
        marginBottom="10px"
      />
      {/* First section - existing layout */}
      <Styled.Container>
        <Styled.MainWrapper>
          <Styled.RightSectionWrapper>
            {mediaItems.length > 3 && (
              <>
                <Styled.BigCardArrowLeft
                  onClick={() => scrollOnce(bigCardScrollRef, "left")}
                  onMouseDown={() => startContinuousScroll(bigCardScrollRef, "left", scrollIntervalRef)}
                  onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                  onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                  title="Scroll Left"
                >
                  <ArrowLeft size={30} />
                </Styled.BigCardArrowLeft>

                <Styled.BigCardArrowRight
                  onClick={() => scrollOnce(bigCardScrollRef, "right")}
                  onMouseDown={() => startContinuousScroll(bigCardScrollRef, "right", scrollIntervalRef)}
                  onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                  onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                  title="Scroll Right"
                >
                  <ArrowRight size={30} />
                </Styled.BigCardArrowRight>

              </>
            )}

            <Styled.HorizontalCardList ref={bigCardScrollRef} scrollable={mediaItems.length > 3}>
              {bigCardMedia.map((media) => (
                <Styled.BigCardItem key={media.id}>
                  <div style={{ position: 'relative' }}>

                    <BigCard
                      customHeight="288px"
                      customBorderRadius="0px"
                      mediaItem={media}
                      mediaRating={mediaRating.find((m) => m.mediaId === media.mediaId) || 0}
                      enableRatingControls={enableRatingControls}
                      isSignedIn={isSignedIn}
                      hasEligiblePlan={
                        isSignedIn &&
                        (!!media.planDetails?.length
                          ? !!activePlans.filter((plan) =>
                            media.planDetails?.some((pd) => pd?.id === plan.id)
                          ).length ||
                          freeTrialPlanIds.includes(media.planDetails[0]?.id) ||
                          hasFullAccess
                          : true)
                      }
                      onWatchClick={onWatchClick}
                      onRatingSuccess={() => handleRatingSuccessLocal(media.id)}
                      sessionToken={sessionToken}
                      onlyShowWatchButton={false}
                      isClickable={false}
                      disableOverlay={false}
                      alignInfoRowStart={false}
                      limitDescriptionLines={true}
                      enableCompetitionMode={enableCompetitionMode}
                      showSeparateMetaLayout={true}
                    />
                    <RatingToast show={toastCardId === media.id} onClose={() => setToastCardId(null)} />
                  </div>
                  <Styled.SidebarCardWrapper>
                    <Styled.CardTitle>{truncateWords(media.title, 40)}</Styled.CardTitle>
                    <Styled.CardTextWrapper>
                      <Styled.CardSubtitleWrapper>
                        {(() => {
                          const rating = mediaRating.find((m) => m.mediaId === media.mediaId)?.rating;
                          const directors = toTitleCase(media?.directors || "");

                          // if (!rating && !directors) return null;
                          // If Competition mode is ON, do not show ratings
                          const shouldShowRating = !enableCompetitionMode && (rating || rating === 0);

                          if (!shouldShowRating && !directors) return null;

                          return (
                            <Styled.CardSubtitleWrapper>
                              {shouldShowRating && <StarFill color={`${colors.text.black}`} />}
                              <Styled.CardSubtitleText>
                                {shouldShowRating ? `${(rating ?? 0)}/5` : ""}
                                {directors && <span>{directors}</span>}
                              </Styled.CardSubtitleText>
                            </Styled.CardSubtitleWrapper>
                          );
                        })()}
                      </Styled.CardSubtitleWrapper>
                    </Styled.CardTextWrapper>
                  </Styled.SidebarCardWrapper>
                </Styled.BigCardItem>
              ))}

              {mediaItems.length > 6 && (
                <Styled.SidebarCardWrapper onClick={handleSeeAllClick}>
                  <SeeCard width="512px" height="288px" />
                </Styled.SidebarCardWrapper>
              )}
            </Styled.HorizontalCardList>
          </Styled.RightSectionWrapper>
        </Styled.MainWrapper>

        <div>
          {/* Second section - card boxes */}
          <div>
            <Styled.SidebarContainer>
              {remainingMedia.length >= 6 && (
                <>
                  <Styled.ArrowLeft
                    onClick={() => scrollOnce(sidebarScrollRef, "left")}
                    onMouseDown={() => startContinuousScroll(sidebarScrollRef, "left", scrollIntervalRef)}
                    onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                    onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                    title="Scroll Left"
                  >
                    <ArrowLeft size={30} />
                  </Styled.ArrowLeft>

                  <Styled.ArrowRight
                    onClick={() => scrollOnce(sidebarScrollRef, "right")}
                    onMouseDown={() => startContinuousScroll(sidebarScrollRef, "right", scrollIntervalRef)}
                    onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                    onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                    title="Scroll Right"
                  >
                    <ArrowRight size={30} />
                  </Styled.ArrowRight>

                </>
              )}

              <Styled.ScrollableList ref={sidebarScrollRef} scrollable={remainingMedia.length > 4}>
                {remainingMedia.slice(0, 6).map((item) => (
                  <Styled.SidebarCardWrapper key={item.id}>
                    <SidebarCard
                      mediaItem={item}
                      cardClick={() => onCardClick(item)}
                      //isSelected={item.id === selectedCardId}
                      isSelected={false}
                      mediaRating={mediaRating.find((m) => m.mediaId === item.mediaId)?.rating || 0}
                      enableRatingControls
                      isSignedIn={isSignedIn}
                      hasEligiblePlan={isSignedIn && !!item.planDetails?.length}
                      onWatchClick={onWatchClick}
                      onRatingSuccess={onRatingSuccess}
                      sessionToken={sessionToken}
                    />

                    {/* Title and Subtitle below the card */}
                    <Styled.CardTextWrapper>
                      <Styled.CardTitleText title={item.title}>{truncateWords(item.title, 20)}</Styled.CardTitleText>
                      {(() => {
                        const rating = mediaRating.find((m) => m.mediaId === item.mediaId)?.rating;
                        const directors = toTitleCase(item?.directors || "");

                        // Only show rating if competition mode is OFF
                        const shouldShowRating = !enableCompetitionMode && (rating || rating === 0);

                        // If no rating and no directors, skip rendering the subtitle
                        if (!shouldShowRating && !directors) return null;

                        return (
                          <Styled.CardSubtitleWrapper>
                            {shouldShowRating && <StarFill color={`${colors.text.black}`} />}
                            <Styled.CardSubtitleText>
                              {shouldShowRating ? `${(rating ?? 0)}/5` : ""}
                              {directors && <span>{directors}</span>}
                            </Styled.CardSubtitleText>
                          </Styled.CardSubtitleWrapper>
                        );
                      })()}
                    </Styled.CardTextWrapper>
                  </Styled.SidebarCardWrapper>
                ))}

                {remainingMedia.length >= 6 && (
                  <Styled.SidebarCardWrapper onClick={handleSeeAllClick}>
                    <SeeCard />
                  </Styled.SidebarCardWrapper>
                )}
              </Styled.ScrollableList>
            </Styled.SidebarContainer>
          </div>
        </div>
      </Styled.Container>

      {/* Modal with Lazy Loaded Cards */}
      <LayoutModal
        title={playlistTitle}
        show={isModalOpen}
        onHide={handleCloseModal}
        remainingMedia={mediaItems}
        onCardClick={onCardClick}
        isSignedIn={isSignedIn}
        activePlans={activePlans}
        hasFullAccess={hasFullAccess}
        freeTrialPlanIds={freeTrialPlanIds}
        mediaRating={mediaRating}
        onWatchClick={onWatchClick}
        onRatingSuccess={onRatingSuccess}
        sessionToken={sessionToken}
      />
    </>
  );
};

